import { CSSProperties, FC, ReactNode, useMemo } from 'react';
import { Checkbox, CheckboxOptionType } from 'antd';
import { CheckboxGroupProps } from 'antd/es/checkbox';
import { FaFacebook, FaGoogle, FaInstagram, FaLinkedin, FaTiktok, FaYoutube } from 'react-icons/fa';
import { IoImageOutline } from 'react-icons/io5';
import { TbCarouselHorizontal, TbDeviceMobile, TbDeviceTv, TbGif, TbQuestionMark, TbVideo } from 'react-icons/tb';
import { BiFilm } from 'react-icons/bi';
import { FaRadio } from 'react-icons/fa6';
import { FormBuilderFieldCheckbox } from 'types/formBuilder';
import useGridColumnsLinear from './useGridColumnsLinear';
import { getResponsiveByFieldName } from './FieldRadio';

const modeClassName: Record<FormBuilderFieldCheckbox['mode'], string> = {
  simple: '',
  pill: 'checkbox-group-pill',
  'pill-checklist': 'checkbox-group-pill-checklist',
  square: 'checkbox-group-square',
};

interface FieldCheckboxProps {
  id?: string;
  value?: CheckboxGroupProps['value'];
  data: FormBuilderFieldCheckbox;
  disabled: boolean;
  onChange?: CheckboxGroupProps['onChange'];
  style?: CSSProperties;
  checklist?: boolean;
}

const listIcons: Record<string, ReactNode> = {
  google: <FaGoogle />,
  youtube: <FaYoutube />,
  facebook: <FaFacebook />,
  instagram: <FaInstagram />,
  radio: <FaRadio />,
  ott: <TbDeviceTv />,
  linkedin: <FaLinkedin />,
  tiktok: <FaTiktok />,
  video: <TbVideo />,
  film: <BiFilm />,
  image: <IoImageOutline />,
  carousel: <TbCarouselHorizontal />,
  gif: <TbGif />,
  question: <TbQuestionMark />,
  phone: <TbDeviceMobile />,
  phoneLandscape: <TbDeviceMobile style={{ rotate: '-90deg' }} />,
};

const FieldCheckbox: FC<FieldCheckboxProps> = ({ id, value, data, disabled, onChange, style, checklist = false }) => {
  const isMobile = window.innerWidth < 768;

  const fallbackResponsive = getResponsiveByFieldName(data.name, []);

  const responsive = {
    mobile: data.responsive?.mobile ?? fallbackResponsive?.mobile ?? { layout: 'column' },
    desktop: data.responsive?.desktop ?? fallbackResponsive?.desktop ?? { layout: 'row' },
  };

  const layout = isMobile ? responsive.mobile : responsive.desktop;

  const options = useMemo(() => {
    return data.options.map(
      (item): CheckboxOptionType => ({
        ...item,
        label: (
          <div style={{ textAlign: data.mode === 'square' ? 'center' : 'left', ...style }}>
            {item.imageUrl ? (
              <>
                <img
                  src={item.imageUrl}
                  style={{
                    display: 'inline-block',
                    width: data.mode === 'square' ? '40px' : 16,
                    marginRight: data.mode !== 'square' ? 6 : undefined,
                    verticalAlign: data.mode !== 'square' ? 'text-bottom' : undefined,
                  }}
                  alt="icon"
                />
                {data.mode === 'square' ? <br /> : undefined}
              </>
            ) : undefined}

            {item.icon ? (
              <>
                {listIcons[item.icon]}
                {data.mode === 'square' ? <br /> : undefined}
              </>
            ) : undefined}

            {item.label}
          </div>
        ),
        onChange: (event) => {
          if (!event.target.checked) {
            return;
          }

          const uniqueOptions = data.options.filter((option) => option.unique);

          if (uniqueOptions.length) {
            const optionTarget = uniqueOptions.find((option) => option.value === event.target.value);

            if (optionTarget) {
              setTimeout(() => {
                onChange?.([optionTarget.value]);
              }, 0);
            } else {
              const filtered = value?.filter((option) => !uniqueOptions.find((uniqueOption) => uniqueOption.value === option));

              if (value?.length !== filtered?.length) {
                setTimeout(() => {
                  onChange?.([...(filtered ?? []), event.target.value]);
                }, 0);
              }
            }
          }
        },
      }),
    );
  }, [data.mode, data.options, onChange, value]);

  const columnsNumber = useGridColumnsLinear({ length: options.length });

  const groupStyle = useMemo<CSSProperties | undefined>(() => {
    const baseStyle: CSSProperties = { gap: '8px' };

    if (data.type === 'checkbox' && data.mode === 'square') {
      return {
        display: 'grid',
        gridTemplateColumns: `repeat(${columnsNumber}, 1fr)`,
        ...baseStyle,
      };
    }

    if (!layout || !layout.layout) return undefined;

    switch (layout.layout) {
      case 'row':
        return {
          display: 'flex',
          flexWrap: 'wrap',
          ...baseStyle,
        };
      case 'column':
        return {
          display: 'flex',
          alignItems: isMobile ? 'inherit' : 'start',
          flexDirection: 'column',
          ...baseStyle,
        };
      case 'grid':
        return {
          display: 'grid',
          gridTemplateColumns: `repeat(${3}, 1fr)`,
          ...baseStyle,
        };
      default:
        return undefined;
    }
  }, [layout, isMobile, columnsNumber, data.mode, data.type]);

  return (
    <div id={id}>
      <Checkbox.Group
        className={`${modeClassName[data.mode]}${disabled ? ' read-only' : ''} ${checklist ? 'checklist-group' : ''}`}
        style={{ ...groupStyle }}
        value={value}
        options={options}
        onChange={(values) => {
          if (data.max && values.length > data.max) {
            return;
          }

          onChange?.(values);
        }}
      />
    </div>
  );
};

export default FieldCheckbox;

export { listIcons };
